import { call, put, all, takeLatest, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import axios from 'axios';
import config from '@/services/Api/apiConfig';
import actions, { actionTypes } from './actions';
import modalsActions from '../modals/actions';
import ApiService from '@/services/Api';
import LocalStorageService from '@/services/LocalStorageService';

function* logIn(action) {
  const { email: email_or_phone, password, autoLogin } = action.payload;
  try {
    const { data } = yield call(ApiService.logIn, { email_or_phone, password });
    if (data.is_terms_accepted === false) {
      yield put(actions.setTermsPolicyPayload(data));
      yield put(modalsActions.togglePrivacyPolicyModal(true));
      ApiService.init(data.token);
      yield put(actions.logInError());
    } else {
      yield put(actions.setTermsPolicyPayload(null));
      yield put(actions.logInSuccess({ data, autoLogin }));
      ApiService.init(data.token);
      LocalStorageService.set('user', data);
      if (autoLogin) {
        LocalStorageService.set('token', data.token);
      }
    }
  } catch (error) {
    yield put(actions.logInError(error));
    LocalStorageService.clear();
  }
}

function* signUp(action) {
  try {
    const { data } = yield call(ApiService.signUp, action.payload);
    yield put(actions.signUpSuccess(data));
    ApiService.init(data.token);
    LocalStorageService.set('user', data);
    LocalStorageService.set('token', data.token);
    yield put(push('/signup-result'));
  } catch (error) {
    yield put(actions.signUpError(error));
    LocalStorageService.clear();
  }
}

function* signOut() {
  try {
    yield put(actions.signOutSuccess());
    LocalStorageService.clear();
    yield put(push('/login'));
  } catch (error) {
    yield put(actions.signOutError());
  }
}

function* checkToken() {
  try {
    const token = LocalStorageService.get('token');
    if (token) {
      yield call(ApiService.init, token);
      const { data } = yield call(ApiService.getUserData);
      yield put(actions.checkTokenSuccess({ data, autoLogin: true, token }));
    } else {
      yield put(actions.cancelCheckToken());
    }
  } catch (error) {
    yield put(actions.checkTokenFailed(error));
    LocalStorageService.clear();
  }
}

function* updateUser({ payload }) {
  try {
    const { data } = yield call(ApiService.updateUser, payload);
    yield put(actions.updateUserSuccess(data));
  } catch (error) {
    yield put(actions.updateUserFailed(error));
  }
}

function* updateOrganization({ payload }) {
  const { pk } = yield select(({ auth }) => ({
    pk: auth.profile.organization.pk,
  }));

  try {
    const { data } = yield call(ApiService.updateOrganization, pk, payload);
    yield put(actions.updateOrganizationSuccess(data));
  } catch (error) {
    yield put(actions.updateOrganizationFailed(error));
  }
}

function* uploadAvatar({ payload }) {
  const { file, onSuccess, token, url } = payload;
  const formData = new FormData();
  let name = 'image';
  let requestUrl = `${config.BASE_URI}/api/${url}/`;

  if (url === 'organization') {
    const { pk } = yield select(({ auth }) => ({
      pk: auth.profile.organization.pk,
    }));
    requestUrl = `${config.BASE_URI}/api/${url}/${pk}/`;
    name = 'logo';
  }

  formData.append(name, file);

  try {
    const { data } = yield call(axios, {
      method: 'patch',
      url: requestUrl,
      data: formData,
      headers: {
        'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
        Authorization: `Token ${token}`,
      },
    });

    yield call(onSuccess, data);
    if (url === 'user') {
      yield put(actions.updateUserSuccess(data));
    } else {
      yield put(actions.updateOrganizationSuccess(data));
    }
  } catch (error) {
    if (url === 'user') {
      yield put(actions.updateUserFailed(error));
    } else {
      yield put(actions.updateOrganizationFailed(error));
    }
  }
}

function* removeAvatar({ payload }) {
  try {
    if (payload === 'user') {
      const { data } = yield call(ApiService.updateUser, { image: null });
      yield put(actions.updateUserSuccess(data));
    } else {
      const { pk } = yield select(({ auth }) => ({
        pk: auth.profile.organization.pk,
      }));
      const { data } = yield call(ApiService.updateOrganization, pk, { logo: null });
      yield put(actions.updateOrganizationSuccess(data));
    }
  } catch (error) {
    if (payload === 'user') {
      yield put(actions.updateUserFailed(error));
    } else {
      yield put(actions.updateOrganizationFailed(error));
    }
  }
}

function* resetPassword({ payload }) {
  try {
    const { data } = yield call(ApiService.resetPassword, payload);
    yield put(actions.resetPasswordSuccess(data));
  } catch (error) {
    yield put(actions.resetPasswordFailed(error));
  }
}

function* sendEmailReset({ payload }) {
  try {
    yield call(ApiService.requestResetPassword, payload);
    yield put(actions.sendEmailResetSuccess());
  } catch (error) {
    yield put(actions.sendEmailResetFailed(error));
  }
}

function* fetchOrganizations() {
  try {
    const { data } = yield call(ApiService.fetchOrganizations);
    yield put(actions.fetchOrganizationsSuccess(data));
  } catch (error) {
    yield put(actions.fetchOrganizationsFailed(error));
  }
}

function* acceptTermsAndPolicy({ payload }) {
  try {
    yield call(ApiService.acceptTermsAndPolicy, payload);
    yield put(actions.acceptTermsAndPolicySuccess());
    yield put(modalsActions.togglePlatformAgreementsModal(false));
  } catch (error) {
    yield put(actions.acceptTermsAndPolicyFailed(error));
  }
}

function* saveSupportInfo({ payload }) {
  const { formData, token } = payload;
  try {
    const { data } = yield call(axios, {
      method: 'post',
      url: `${config.BASE_URI}/api/support_info/`,
      data: formData,
      headers: {
        'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
        Authorization: `Token ${token}`,
      },
    });

    yield put(actions.saveSupportInfoSuccess(data));
  } catch (error) {
    yield put(actions.saveSupportInfoFailed(error));
  }
}

export default function* () {
  yield all([
    takeLatest(actionTypes.LOG_IN, logIn),
    takeLatest(actionTypes.SIGN_UP, signUp),
    takeLatest(actionTypes.SIGN_OUT, signOut),
    takeLatest(actionTypes.CHECK_TOKEN, checkToken),
    takeLatest(actionTypes.UPDATE_USER, updateUser),
    takeLatest(actionTypes.UPDATE_ORGANIZATION, updateOrganization),
    takeLatest(actionTypes.AVATAR_UPLOAD, uploadAvatar),
    takeLatest(actionTypes.REMOVE_AVATAR, removeAvatar),
    takeLatest(actionTypes.RESET_PASSWORD, resetPassword),
    takeLatest(actionTypes.SEND_EMAIL_RESET, sendEmailReset),
    takeLatest(actionTypes.FETCH_ORGANIZATIONS, fetchOrganizations),
    takeLatest(actionTypes.ACCEPT_TERMS_AND_POLICY, acceptTermsAndPolicy),
    takeLatest(actionTypes.SAVE_SUPPORT_INFO, saveSupportInfo),
  ]);
}
